import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, Observable, of, Subject, switchMap, takeWhile, timer } from "rxjs";
import { EDashboardStatus, TDashboardStatus } from "src/app/pages/dashboard/dashboard.constants";
import { LoggerService } from "src/app/services/logger.service";
import { CommonUtils } from "src/app/utils/CommonUtils";
import { environment } from "src/environments/environment";
import { EProductType, ICancelDocumentResponse, ICompanyDocumentResponse, IDocument, IIbanObject, IS3DownloadInfo, MULTISOLUTION_PRODUCTS } from "../athora/athora.constants";
import { SearchSubject, SearchSubjectResponse } from '../isv/components/beneficiary-form-isv/beneficiary-form-isv.constants';
import { IAnagrafica } from "../shared/components/ndg-variation/ndg-variation.constants";
import { IDashboardOperation, IDraft, IOperationConstraint, IOperationsConstraints, IProductConstraint } from "../shared/models/operation.model";
import { EPolicyCompany, EPolicyStatus, EPolicyTypes, ESubjectType, ICityObject, IFondo, ILegalPersonIntesa, IPersonIntesa, IPolicyAsset, IPolicyDetail, IPolicyDetailSubject, IPolicyDocument, IPolicyOperation, Uni30Quota } from "../shared/models/policy.model";
import { IUploadDocumentObj } from './../athora/components/document-upload/document-upload.constants';
import { ADESION_TYPE_LABEL, IPolicyKeys, MULTIRAMO_uni20_countervalue_form_keys, PEGNO_VINCOLO_KEY, PEGNO_VINCOLO_LABEL, policy_detail_multiramo, policy_detail_previdenziale, policy_detail_previdenziale_uni30, policy_detail_tcm, policy_detail_tradizionale, policy_detail_unit, PREV_uni20_compartment_data_keys, TCM_VITA_PRODUCT_CODE, TCM_VITA_PRODUCT_DESCRIPTION_PF, TCM_VITA_PRODUCT_DESCRIPTION_PG, TRAD_uni20_countervalue_form_keys, tutor_form_keys_PF, tutor_form_keys_PG, UNIT_uni20_countervalue_form_keys } from "./pages/policy-detail/policy-detail.constants";
import moment = require('moment');

@Injectable({
  providedIn: 'root'
})
export class PolicyService {

  private _policyId: string = null;
  private _operationId: string = null;
  private _policyDetail: IPolicyDetail = null;
  private _subject: IPolicyDetailSubject = null;
  private _formattedLegalSubjectKeys: any = {};
  private _ndg: string = null;
  private _companyId: string = null;
  private _policyType: EPolicyTypes = null;
  private _operations: IPolicyOperation[] = [];
  private _operationsConstraints: IOperationsConstraints[];
  private _canOperateToday: boolean = true;
  private _detailLoaded$ = new BehaviorSubject<boolean>(false);
  private _refreshDetail$: Subject<boolean> = new Subject<boolean>();
  private _retrieveDocuments$: Subject<void> = new Subject<void>();
  private _retrieveDownloadInfo$: Subject<IDocument> = new Subject<IDocument>();
  private _documentDownload$: Subject<IDocument> = new Subject<IDocument>();
  private _documentUpload$: Subject<IUploadDocumentObj> = new Subject<IUploadDocumentObj>();
  private _transactionList: IDashboardOperation[];
  private _policyHasConstraint: boolean = false;
  private _operationToCancel: string = null;
  private _contractNumber: string = null;

  constructor(
    private readonly _http: HttpClient
  ) { }

  get isLocalEnv(): boolean {
    return environment.name === 'LOCAL' || environment.name === 'LOCAL_BE';
  }

  get policyId(): string {
    return this._policyId;
  }

  get policyDetail(): IPolicyDetail {
    return this._policyDetail;
  }

  get operationId(): string {
    return this._operationId;
  }

  get subject(): IPolicyDetailSubject {
    return this._subject;
  }

  get ndg(): string {
    return this._ndg;
  }

  get companyId(): string {
    return this._companyId;
  }

  get policyType(): EPolicyTypes {
    return this._policyType;
  }

  get productCode(): string {
    return CommonUtils.checkAndGet(this.policyDetail, 'summary.productCode', null);
  }

  get operations(): IPolicyOperation[] {
    return this._operations;
  }

  set transactionList(value: IDashboardOperation[]) {
    this._transactionList = value;
  }

  get transactionList() {
    return this._transactionList;
  }

  get operationsConstraints(): IOperationsConstraints[] {
    return this._operationsConstraints;
  }

  get canOperateToday(): boolean {
    return this._canOperateToday;
  }

  set canOperateToday(value: boolean) {
    this._canOperateToday = value;
  }

  get isLegalSubject(): boolean {
    return CommonUtils.checkAndGet(this.subject, 'type', null) === ESubjectType.PG;
  }

  get legalSubjects(): IPolicyDetailSubject[] {
    return CommonUtils.checkAndGet(this.policyDetail, 'legalSubjects', null);
  }

  get formattedLegalSubjectKeys(): any {
    return this._formattedLegalSubjectKeys;
  }

  get accountHolder(): string {
    return this.isLegalSubject ?
      CommonUtils.checkAndGet(this.subject, 'companyName', null) :
      CommonUtils.checkAndGet(this.subject, 'name', null) + ' ' + CommonUtils.checkAndGet(this.subject, 'surname', null);
  }

  get isAthoraPolicy(): boolean {
    return this.companyId === EPolicyCompany.ATHORA;
  }

  get detailLoadedObs(): Observable<boolean> {
    return this._detailLoaded$.asObservable();
  }

  get refreshDetail(): Observable<boolean> {
    return this._refreshDetail$.asObservable();
  }

  get policyHolderIntesa(): IPersonIntesa | ILegalPersonIntesa {
    return CommonUtils.checkAndGet(this.policyDetail, `intesaFurtherDetails.${this.isDetailPrevidenziale ? 'uni30' : 'uni20'}.policyHolder`, null);
  }
  get legalGuardianIntesa(): IPersonIntesa {
    return CommonUtils.checkAndGet(this.policyDetail, `intesaFurtherDetails.${this.isDetailPrevidenziale ? 'uni30' : 'uni20'}.legalGuardian`, null);
  }

  get isDetailPrevidenziale(): boolean {
    const furtherDetails = CommonUtils.checkAndGet(this.policyDetail, 'intesaFurtherDetails', {}) as object
    return Object.prototype.hasOwnProperty.call(furtherDetails, 'uni30');
  }

  get isDetailUni20(): boolean {
    const furtherDetails = CommonUtils.checkAndGet(this.policyDetail, 'intesaFurtherDetails', {}) as object
    return Object.prototype.hasOwnProperty.call(furtherDetails, 'uni20');
  }

  get retireveDocumentsSubscription(): Observable<void> {
    return this._retrieveDocuments$.asObservable();
  }

  get retireveDownloadInfoSubscription(): Observable<IDocument> {
    return this._retrieveDownloadInfo$.asObservable();
  }

  get documentDownloadSubscription(): Observable<IDocument> {
    return this._documentDownload$.asObservable();
  }

  get documentUploadSubscription(): Observable<IUploadDocumentObj> {
    return this._documentUpload$.asObservable();
  }

  get policyHasConstraint(): boolean {
    return this._policyHasConstraint;
  }

  get operationToCancel(): string {
    return this._operationToCancel;
  }

  get contractNumber(): string {
    return this._contractNumber;
  }

  set contractNumber(value: string) {
    this._contractNumber = value;
  }

  setOperationToCancel(op: string): void {
    this._operationToCancel = op;
  }

  refreshPolicyDetail(value: boolean): void {
    this._refreshDetail$.next(value);
  }

  detailLoaded(value: boolean): void {
    this._detailLoaded$.next(value);
  }

  retrieveDocuments(): void {
    this._retrieveDocuments$.next();
  }

  retrieveDocumentInfo(document: IDocument): void {
    this._retrieveDownloadInfo$.next(document);
  }

  setRouteIds(id: string, op: string) {
    this.contractNumber = id;
    this._operationId = op;
    LoggerService.logInfo(this.constructor.name, 'setRouteIds', { id, op });
  }

  setDetail(resp: IPolicyDetail) {
    this._policyDetail = resp;
    this._subject = CommonUtils.checkAndGet(resp, 'policyHolder', null);
    this._ndg = CommonUtils.checkAndGet(resp, 'policyExtraInfo.ndg', null);
    this._companyId = CommonUtils.checkAndGet(resp, 'summary.companyCode', null);
    this._policyType = CommonUtils.checkAndGet(resp, 'summary.policyType', null);
    this._operations = CommonUtils.checkAndGet(resp, 'permittedOperations', null);
    this._policyId = CommonUtils.checkAndGet(resp, 'summary.bperPolicyNumber', null);
    this._contractNumber = CommonUtils.checkAndGet(resp, 'policyExtraInfo.contractNumber', null);

    this.managePolicyDetail();
    this.manageOperations();

    this.detailLoaded(true);
    LoggerService.logInfo(this.constructor.name, 'setDetail', { resp });
  }

  managePolicyDetail(): void {
    if (this._policyDetail) {
      if (this.productCode === TCM_VITA_PRODUCT_CODE) {
        if (this.isLegalSubject) {
          this._policyDetail.summary.productDescription = TCM_VITA_PRODUCT_DESCRIPTION_PG;
        } else {
          this._policyDetail.summary.productDescription = TCM_VITA_PRODUCT_DESCRIPTION_PF;
        }
      }
    }
  }

  manageOperations(): void {
    if (this._transactionList && this._transactionList.length) {
      // check per VBESC
      const lastOperation = this._transactionList[0];
      if (lastOperation) {
        if (lastOperation.operationId === '_VBESC' && lastOperation.status !== EDashboardStatus.CANCELLED) {
          this._operations = this._operations?.filter(op => op.operationCode !== '_VBESC');
        }
      }
      // check per vincolo polizza
      if (this.isAthoraPolicy) {
        let vincoloOps = this._transactionList.filter(op =>
          op.operationDescription === "Apertura Vincolo" || op.operationDescription === "Chiusura Vincolo"
        );
        vincoloOps.sort((a, b) => moment(a.effectDate).valueOf() - moment(b.effectDate).valueOf());
        this._policyHasConstraint = vincoloOps.length > 0 && vincoloOps[vincoloOps.length - 1].operationDescription === "Apertura Vincolo";
      } else {
        this._policyHasConstraint = CommonUtils.checkAndGet(this.policyDetail, 'intesaFurtherDetails.uni20.pegnoVincolo', 'NO') === 'SI';
      }
    }
  }

  managePolicyDocuments(documents: IPolicyDocument[]): void {
    if (this._policyDetail) {
      this._policyDetail.documents = CommonUtils.assign(documents);
    }
  }

  getDetailKeys(type: EPolicyTypes): IPolicyKeys {
    let keys: IPolicyKeys = null;
    switch (type) {
      case EPolicyTypes.PREVIDENZIALE:
      case EPolicyTypes.FONDO_PENSIONE:
      case EPolicyTypes.PREVIDENZIALI:
        if (this.isDetailPrevidenziale) {
            keys = CommonUtils.assign(policy_detail_previdenziale_uni30)
        } else if (this.isDetailUni20){
            keys = CommonUtils.assign(policy_detail_tradizionale);
            keys.header.push({
                label: ADESION_TYPE_LABEL,
                key: "intesaFurtherDetails.uni20.adesionType"
              })
            keys.accordions.unshift({
                id: 'POLICY',
                label: 'Dati comparti',
                keys: PREV_uni20_compartment_data_keys,
            })
        } else {
            keys = CommonUtils.assign(policy_detail_tradizionale);
        }
        break;
      case EPolicyTypes.PIP:
        keys = CommonUtils.assign(policy_detail_previdenziale);
        break;
      case EPolicyTypes.TCM:
      case EPolicyTypes.TCM_PPI:
        keys = CommonUtils.assign(policy_detail_tcm);
        break;
      case EPolicyTypes.UNIT:
        keys = CommonUtils.assign(policy_detail_unit);
        if (this.isDetailUni20) {
          keys.header.push({
            label: PEGNO_VINCOLO_LABEL,
            key: PEGNO_VINCOLO_KEY
          })
          this.handleAccordion(keys, UNIT_uni20_countervalue_form_keys, 'COUNTERVALUE');
        }
        break;
      case EPolicyTypes.MULTIRAMO:
        keys = CommonUtils.assign(policy_detail_multiramo);
        if (this.isDetailUni20) {
            keys.header.push({
            label: PEGNO_VINCOLO_LABEL,
            key: PEGNO_VINCOLO_KEY
          })
          this.handleAccordion(keys, MULTIRAMO_uni20_countervalue_form_keys, 'COUNTERVALUE');
        }
        break;
      case EPolicyTypes.TRADIZIONALE:
      default:
        keys = CommonUtils.assign(policy_detail_tradizionale);
        if (this.isDetailUni20) {
          keys.header.push({
            label: PEGNO_VINCOLO_LABEL,
            key: PEGNO_VINCOLO_KEY
          })
          this.handleAccordion(keys, TRAD_uni20_countervalue_form_keys, 'COUNTERVALUE');
        }
        break;
    }
    // Check per sezione "Tutore"
    this.handleLegalSubjects(keys);
    // Check per campo "Polizza differita"
    if (this.productCode !== 'BPMF01') {
      this.removeKeyFromAccordion(keys, 'POLICY', 'summary.isPolicyDeferred');
    }
    // Check per campo "Allocazione rami" (solo per Multisoluzione)
    if (this.policyType === EPolicyTypes.MULTIRAMO && !MULTISOLUTION_PRODUCTS.includes(EProductType[this.productCode])) {
      this.removeKeyFromAccordion(keys, 'COUNTERVALUE', 'summary.currentRiskDescription');
    }
    // Check per stato della polizza
    if (this.policyDetail.summary.policyStatus !== EPolicyStatus.ATTIVA) {
      this.removeKeyFromAccordion(keys, 'COUNTERVALUE', 'policyValue');
    }
    return keys;
  }

  removeKeyFromAccordion(keys: any, accordionId: string, keyToRemove: string): void {
    const accordion = keys.accordions.find(acc => acc.id === accordionId);
    if (accordion) {
      accordion.keys = accordion.keys.filter(key => key.key !== keyToRemove);
    }
  };

  handleLegalSubjects(keys: any): void {
    if (CommonUtils.isVoidObj(this.legalSubjects)) {
      keys.accordions = keys.accordions.filter(accordion => accordion.id !== 'TUTOR');
    } else {
      this.legalSubjects.forEach((legalSubject, index) => {
        this._formattedLegalSubjectKeys[index] = legalSubject.type === 'PF' ? tutor_form_keys_PF : tutor_form_keys_PG;
      })
    }
  }

  handleAccordion(keys: any, accordionKeys: any, accordionId: string): void {
    if (keys && accordionKeys) {
      const accordionToReplace = keys.accordions.find(acc => acc.id === accordionId);
      if (accordionToReplace) {
        accordionToReplace.keys = CommonUtils.assign(accordionKeys);
      }
    }
  }

  setOperationsConstraints(operationsContraints: IProductConstraint[]): void {
    LoggerService.logInfo(this.constructor.name, 'setOperationsConstraints', operationsContraints);
    this._operationsConstraints = CommonUtils.assign(operationsContraints);
    if (this._operationsConstraints) {
      Object.keys(this._operationsConstraints).forEach((operationKey: string) => {
        let constraints: IOperationConstraint[] = [];
        constraints = this._operationsConstraints[operationKey].map(constraint => constraint.role);
        if (constraints && constraints.length > 0) {
          // rimuovo le operazioni che hanno state = false
          if (constraints.some(constraint => !constraint.state)) {
            this._operations = this.operations.filter(operation => operation.operationCode !== operationKey);
          }
        }
      });
    }
  }

  clearAll() {
    this._policyId = null;
    this._policyDetail = null;
    this._subject = null;
    this._operationId = null;
    this._ndg = null;
    this._companyId = null;
    this._policyType = null;
    this._operations = [];
    LoggerService.logInfo(this.constructor.name, 'clearAll');
  }

  getPolicyById(): Observable<IPolicyDetail> {
    if (this.isLocalEnv) {
      return this._http.get<IPolicyDetail>(environment.apiBaseUrl + '/policyById' + `/${this._policyId}`);
    }
    const url = environment.apiBaseUrl + environment.apiPaths.policies + `/${this._policyId}`;
    return this._http.get<IPolicyDetail>(url);
  }

  getPolicyByContractNumber(): Observable<IPolicyDetail> {
    if (this.isLocalEnv) {
      return this._http.get<IPolicyDetail>(environment.apiBaseUrl + '/policyById' + `/${this._contractNumber}`);
    }
    const url = environment.apiBaseUrl + environment.apiPaths.policies + `/${this._contractNumber}`;
    return this._http.get<IPolicyDetail>(url);
  }

  retrievePolicyDocuments(policyNumber: string): Observable<IPolicyDocument[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.policyDocuments;
    const params = new HttpParams()
      .set('policyNumber', policyNumber);
    return this._http.get<any>(url, {
      params: params
    });
  }

  getMovements(contractNumber: string, datariferimento: moment.Moment): Observable<Uni30Quota[]>{
    const url = environment.apiBaseUrl + environment.apiPaths.movements;
    const params = new HttpParams()
      .set('contractNumber', contractNumber)
      .set('datariferimento', datariferimento.format('YYYY-MM-DDTHH:mm:ss'));
    return this._http.get<Uni30Quota[]>(url, {
      params: params
    });
  }

  retrieveNdgList(contractNumber: string, ndg?: string): Observable<IAnagrafica[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.retrieveNdgs;
    let params = new HttpParams()
      .set('bperContractNumber', contractNumber);
    if (ndg) {
      params = params.set('ndg', ndg);
    }
    return this._http.get<any>(url, {
      params: params
    });
  }

  searchSubjects(contractNumber: string, user: SearchSubject, rows: string, offset: string): Observable<SearchSubjectResponse[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.ricercaSoggetto;
    return this._http.post<any>(url, { ...user, contractNumber: contractNumber, offset, rows});
  }

  retrieveIbanList(contractNumber: string, ndg?: string): Observable<IIbanObject[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.retrieveIbanList;
    let params = new HttpParams()
      .set('bperContractNumber', contractNumber);
    if (ndg) {
      params = params.set('ndg', ndg);
    }
    return this._http.get<any>(url, {
      params: params
    });
  }

  getDraft(movementId: string, isProfileHD: boolean): Observable<IDraft> {
    let url = environment.apiBaseUrl + environment.apiPaths.draft + `?contractNumber=${this.contractNumber}`;
    if (!CommonUtils.isVoid(movementId)) {
      url += `&movementId=${movementId}`;
    }
    if (isProfileHD && movementId === null) {
      url += `&isProfileSimulationHD=${isProfileHD}`;
    }
    if (this.isLocalEnv) {
      return this._http.get<IDraft>('/mock/getDraft');
    }
    return this._http.get<IDraft>(url);
  }

  saveDraft(draft: string, state: TDashboardStatus, operationId: string): Observable<IDraft> {
    const url = environment.apiBaseUrl + environment.apiPaths.draft;
    const body: IDraft = {
      draft: draft,
      operationCode: operationId,
      operationState: state,
      contractNumber: this.contractNumber
    }
    if (this.isLocalEnv) {
      return this._http.get<any>('/mock/saveDraft');
    }
    return this._http.post<any>(url, body);
  }

  deleteDraft(): Observable<any> {
    const url = environment.apiBaseUrl + environment.apiPaths.draft + `?policyNumber=${this._policyId}` + `&contractNumber=${this.contractNumber}` + `&companyCode=${this._companyId}`;
    if (this.isLocalEnv) {
      return this._http.get<any>('/mock/saveDraft');
    }
    return this._http.delete<any>(url);
  }

  retrieveOperationsContraints(operationId?: string): Observable<IProductConstraint[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.operationsConstraints;
    let params: HttpParams = new HttpParams()
      .set('bperContractNumber', CommonUtils.checkAndGet(this._policyDetail, 'policyExtraInfo.contractNumber', null));
    if (operationId) {
      params = params.set('operationTypeCode', operationId);
    }
    return this._http.get<IProductConstraint[]>(url, {
      params: params
    });
  }

  downloadPolicyDocument(policyId: string, documentId: string): Observable<IPolicyDocument[]> {
    if (this.isLocalEnv) {
      return of(null);
    }
    const url = environment.apiBaseUrl + environment.apiPaths.downloadPolicyDocument;
    const params = new HttpParams()
      .set('policyNumber', policyId)
      .set('documentId', documentId);
    return this._http.get<IPolicyDocument[]>(url, {
      params: params
    });
  }

  getFondo(contractNumber: string, codiceFondo: string): Observable<IFondo>{
    const url = environment.apiBaseUrl + environment.apiPaths.fondo;
    const params = new HttpParams()
      .set('contractNumber', contractNumber)
      .set('codiceFondo', codiceFondo);
    return this._http.get<IFondo>(url, {
      params: params
    });
  }

  retrieveCities(province: string): Observable<ICityObject[] | any> {
    const url = this.companyId === EPolicyCompany.ATHORA
     ? environment.apiBaseUrl + environment.apiPaths.retrieveCities
     : environment.apiBaseUrl + environment.apiPaths.retrieveCitiesIntesa;

    if (this.isLocalEnv) {
      return this._http.get<any[]>(url);
    }
    let params = new HttpParams()
      .set('provinceCode', province);

    if (this.companyId !== EPolicyCompany.ATHORA) {
        params = params.set('policyNumber', this.policyId);
    }
    return this._http.get<ICityObject[]>(url,
      { params: params }
    );
  }

  retrieveUploadDocuments(policyId: string, operationId: string, movementId: string, isCancel: boolean = false): Observable<IDocument[]> {
    const url = environment.apiBaseUrl + environment.apiPaths.documents;
    let params = new HttpParams()
      .set('policyId', policyId)
      .set('operationCode', operationId)
      .set('movementId', movementId);

    if (isCancel) {
        params = params.set('operationToCancel', this.operationToCancel);
    }
    return this._http.get<IDocument[]>(url, {
      params: params
    });
  }

  retrieveDownloadInfo(documentId: string, operationId: string, movementId: string, policyId: string): Observable<HttpResponse<IS3DownloadInfo>> {
      const url = environment.apiBaseUrl + environment.apiPaths.retrieveDocInfo;
      if (this.isLocalEnv) {
        return this._http.get<any>(url);
      }
      const params = new HttpParams()
        .set('documentId', documentId)
        .set('policyNumber', policyId)
        .set('operationCode', operationId)
        .set('movementId', movementId);
      return this._http.get<any>(url, {
        params: params,
        observe: 'response'
      });
    }

  retrieveDownloadInfoStatus(documentId: string, policyId: string, movementId: string): Observable<HttpResponse<any>> {
    const url = environment.apiBaseUrl + environment.apiPaths.retrieveDocInfoStatus;
    if (this.isLocalEnv)
      return this._http.get<any>(url);
    const params = new HttpParams()
      .set('documentId', documentId)
      .set('policyNumber', policyId)
      .set('movementId', movementId);
    return this._http.get<any>(url, {
      params: params,
      observe: 'response'
    });
  }

  pollDocumentStatus(documentId: string, policyId: string, movementId: string): Observable<any> {
    return timer(0, 5000).pipe(
      switchMap(() => this.retrieveDownloadInfoStatus(documentId, policyId, movementId)),
      takeWhile(response => response.status === 202, true)
    );
  }

  startPolling(documentId: string, operationId: string, movementId: string, policyId: string): Observable<HttpResponse<ICompanyDocumentResponse>> {
    return this.retrieveDownloadInfo(documentId, operationId, movementId, policyId)
      .pipe(
        switchMap(infoResponse => {
          if (infoResponse && infoResponse.status === 202) {
            return this.pollDocumentStatus(documentId, policyId, movementId)
            .pipe(
              switchMap(pollResponse => {
                if (pollResponse && pollResponse.status === 200) {
                  return this.retrieveDownloadInfo(documentId, operationId, movementId, policyId);
                }
                else {
                  return of(pollResponse);
                }
              }),
              catchError(error => {
                LoggerService.logError('Errore in fase di recupero del documento', 'pollDocumentStatus', error);
                return of(error);
              })
            )
          } else {
            return of(infoResponse);
          }
        }),
        catchError(error => {
          LoggerService.logError('Errore in fase di recupero del documento', 'startPolling', error);
          return of(error)
        })
    );
  }

  downloadDocument(downloadUrl: string): Observable<Blob> {
    if (this.isLocalEnv) {
      return of(new Blob(['Testo di prova'], { type: 'text/plain' }));
    }
    const headers = new HttpHeaders()
      .set('skipAuthorization', 'true');
    return this._http.get<Blob>(downloadUrl, {
      headers: headers,
      responseType: 'blob' as 'json'
    });
  }

  setDownloadedDoc(body: any): Observable<any> {
    const url = environment.apiBaseUrl + environment.apiPaths.documentDownloadStatusUpdate;
    return this._http.patch<any>(url, body);
  }

  downloadDoc(document: IDocument): void {
    this._documentDownload$.next(document);
  }

  uploadDoc(uploadDocumentObject: IUploadDocumentObj): void {
    this._documentUpload$.next(uploadDocumentObject);
  }

  uploadDocument(url: string, body: FormData, headers: HttpHeaders): Observable<any> {
    if (url) {
      if (this.isLocalEnv) {
        return this._http.get<any>(url);
      }
      return this._http.post<any>(url, body, {
        headers: headers
      });
    }
  }

  finalizeOperation(policyId: string, operationId: string, movementId: string, isCancel: boolean = false): Observable<any> {
    const url = environment.apiBaseUrl + environment.apiPaths.finalizeOperation;

        let params = new HttpParams()
        .set('policyNumber', policyId)
        .set('operationCode', operationId)
        .set('movementId', movementId);

        if (isCancel) {
            params = params.set('operationToCancel', this.operationToCancel);
        }
        if (this.isLocalEnv) {
            return this._http.get<any>(url);
        }
        return this._http.post<any>(url, null, {
            params: params
        });
  }

  checkDocuments(policyNumber: string, operationId: string, productCode: string): Observable<any> {
    const url = environment.apiBaseUrl + environment.apiPaths.checkDocuments;
    if (this.isLocalEnv) {
      return this._http.get<any>(url);
    }

    return this._http.post<any>(url, {
      policyNumber: policyNumber,
      operationCode: operationId,
      productCode: productCode,
    });
  }

  retrieveCancelDocuments(policyNumber: string, documentId: string, movementId: string): Observable<ICancelDocumentResponse> {
    const url = environment.apiBaseUrl + environment.apiPaths.retrieveCancelDocuments;
    if (this.isLocalEnv) {
      return this._http.get<ICancelDocumentResponse>(url);
    }
    const params = new HttpParams()
      .set('policyNumber', policyNumber)
      .set('documentId', documentId)
      .set('movementId', movementId);
    return this._http.get<ICancelDocumentResponse>(url, {
      params: params
    });
  }

  createEmployerCode(payload: any) {
    const url =
      environment.apiBaseUrl + environment.apiPaths.intesa.employerCode;
    if (this.isLocalEnv) {
      return this._http.get<any>(url);
    }
    return this._http.post<any>(url, payload);
  }

  saveAgencyReferent(payload: any) {
    const url =
      environment.apiBaseUrl + environment.apiPaths.intesa.agencyReferent;
    if (this.isLocalEnv) {
      return this._http.get<any>(url);
    }
    return this._http.post<any>(url, payload);
  }
  retrieveFunds(contractNumber: string, operationCode: string, amount: string, lineCode?: string): Observable<any> {
    const url = environment.apiBaseUrl + environment.apiPaths.isvRetrieveFunds;
    if (this.isLocalEnv) {
      return this._http.get<IPolicyAsset>(url);
    }
    let params = new HttpParams()
      .set('contractNumber', contractNumber)
      .set('operationCode', operationCode)
      .set('amount', amount);

    if (lineCode) {
      params = params.set('lineCode', lineCode);
    }

    return this._http.get<IPolicyAsset>(url, {
      params: params
    });
  }

}
